const post = {
  slug: "instagram-catalog-tips-photos-sizes",
  title: "Instagram Catalog Tips: Photos, Names and Sizes That Help Customers Buy",
  seoTitle: "Instagram Catalog Tips: Photos, Names, Sizes",
  description:
    "Your product photos, names and sizes decide whether a DM checkout feels easy. Practical catalog tips for Instagram sellers.",
  excerpt:
    "In a DM checkout, your catalog is your shop window. Here is how to set up photos, names, sizes and stock so customers can find and buy things without asking questions.",
  category: "Catalog",
  tags: ["Instagram catalog", "Product photos", "Product variants"],
  date: "2026-10-12",
  cover: {
    src: "/blog-images/instagram-catalog-tips-photos-sizes-cover.png",
    alt: "A phone showing a product catalog with photos, prices and sizes inside an Instagram DM",
  },
  blocks: [
    {
      type: "p",
      text:
        "When customers shop through your DMs, they do not scroll a website. They see your products one by one inside a chat, which means every photo, name and price has to do its job quickly.",
    },
    {
      type: "p",
      text:
        "A well-organised catalog answers questions before they are asked. A messy one creates the very back-and-forth you were trying to avoid.",
    },

    { type: "h2", text: "Name products the way customers search" },
    {
      type: "p",
      text:
        "Customers can browse your catalog by category or search by product name, so your names should use the words people actually type. Colour, type and material beat internal codes and marketing slogans.",
    },
    {
      type: "table",
      headers: ["Instead of", "Try"],
      rows: [
        ["Item 14", "Linen Kurta Set, Sage Green"],
        ["Summer special", "Cotton Co-ord Set, Cream"],
        ["New arrival!!", "Handmade Silver Jhumkas"],
      ],
    },
    {
      type: "figure",
      src: "/blog-images/instagram-catalog-tips-photos-sizes-figure.png",
      alt: "Comparison of a hard-to-sell product listing and an easy-to-sell product listing",
      caption: "Small changes in a listing make it much easier to buy.",
      width: 1200,
      height: 760,
    },

    { type: "h2", text: "Photos: one clear lead image" },
    {
      type: "ul",
      items: [
        "**Use a consistent shape.** Square photos in the same style make the catalog feel like one shop.",
        "**Light it well.** Natural daylight beats flash almost every time.",
        "**Keep the background simple.** The product should fill the frame, not compete with a busy room.",
        "**Show scale or fit.** A hand, a model or a ruler answers questions a flat photo cannot.",
      ],
    },
    {
      type: "p",
      text:
        "If you have many photos to add, Pro merchants can upload up to 50 product photos at once and turn them into draft products. See the [bulk photo upload guide](/bulk-image-upload-guide).",
    },

    { type: "h2", text: "Sizes and variants" },
    {
      type: "ol",
      items: [
        "**List every size you sell.** Customers choose a size right after they pick a product, so the options should be complete.",
        "**Track stock per size.** Checkout DM tracks stock for each size, so a size that has sold out is not offered to customers.",
        "**Have a size guide ready.** Most size questions are the same question. Put the answer where customers can find it.",
      ],
    },

    { type: "h2", text: "Categories keep a bigger catalog easy" },
    {
      type: "p",
      text:
        "Once you have more than a handful of products, categories help customers browse inside the chat instead of scrolling past everything. Aim for a few clear groups, such as Kurtas, Co-ords and Accessories, rather than a long list of narrow ones.",
    },

    { type: "h2", text: "Keep stock honest" },
    {
      type: "p",
      text:
        "Nothing hurts trust like a customer picking a product and hearing it is gone. Update stock as you sell offline, too. Growth and Pro plans also include low-stock alerts, so you hear about a running-low item before it sells out.",
    },

    { type: "h2", text: "Share a catalog page, too" },
    {
      type: "p",
      text:
        "Paid plans include a public catalog page that customers can browse before they message you, while the DM handles the actual checkout. Put it in your bio so people can look around first. See the [public catalog page guide](/public-catalog-guide) for how it works. If you have no website yet, our guide to [how to sell on Instagram without a website](/blog/sell-on-instagram-without-a-website) shows where the catalog page fits.",
    },
    {
      type: "callout",
      tone: "peach",
      title: "Catalog checklist",
      text:
        "Clear product name, one good photo, a price, every size listed, accurate stock and a category. If each product has those six things, your DM checkout will feel effortless.",
    },

    { type: "h2", text: "A listing template you can copy" },
    {
      type: "p",
      text:
        "Consistency is what makes a catalog feel professional. Use the same fields, in the same order, for every product.",
    },
    {
      type: "ol",
      items: [
        "**Name:** type, colour and material, such as \u201CLinen Kurta Set, Sage Green\u201D.",
        "**Price:** the final price a customer will pay for the item.",
        "**Photo:** one clear lead image on a simple background.",
        "**Sizes or variants:** every option you actually sell, with stock for each.",
        "**Category:** one clear group, such as Kurtas or Accessories.",
        "**Short description:** one or two lines on fabric, fit or what is included.",
      ],
    },

    { type: "h2", text: "A simple photo shot list" },
    {
      type: "p",
      text:
        "You do not need a studio. A phone, daylight and a plain wall or a sheet of paper are enough. For each product, aim for these shots:",
    },
    {
      type: "ul",
      items: [
        "**The lead image.** The whole product, centred, in even daylight. This is the photo customers see first.",
        "**A close-up.** Texture, stitching, print or finish, which shows quality in a way a full shot cannot.",
        "**In use.** The product worn, held or used, so customers can judge size and style.",
        "**Scale.** Something that shows how big or small it really is.",
        "**The back or inside.** For anything where customers would otherwise ask, \u201CWhat does the other side look like?\u201D",
      ],
    },
    {
      type: "p",
      text:
        "Edit lightly. A little brightness and a straight crop help, but heavy filters make colours misleading, and misleading colours cause returns.",
    },

    { type: "h2", text: "Handling colours, sizes and variants" },
    {
      type: "p",
      text:
        "A product that comes in several colours and sizes can be listed in more than one way. Treat each choice the customer must make as a separate, clear step.",
    },
    {
      type: "ul",
      items: [
        "**Sizes:** list every size, and keep a size guide ready, since that is the most common question.",
        "**Colours:** if each colour has its own photo, list each as its own product so the customer sees the exact item they are buying.",
        "**Bundles and sets:** name what is included, so nobody is surprised by what arrives.",
      ],
    },

    { type: "h2", text: "A restock and update routine" },
    {
      type: "p",
      text:
        "A catalog is never finished. A short weekly check keeps it trustworthy.",
    },
    {
      type: "ol",
      items: [
        "Update stock for anything you sold outside Instagram.",
        "Hide or mark products that are no longer available.",
        "Add new arrivals with the same template.",
        "Refresh photos for your best sellers if the lighting or styling has improved.",
        "Check your most-asked questions and add the answers to your listings.",
      ],
    },

    { type: "h2", text: "Common catalog mistakes" },
    {
      type: "ul",
      items: [
        "**Vague names** that customers cannot search for.",
        "**Inconsistent photos** that make the shop look unfinished.",
        "**Missing sizes** that lead to avoidable questions.",
        "**Out-of-date stock** that creates disappointed customers.",
        "**Too many products too soon.** A small, accurate catalog beats a large, messy one.",
      ],
    },
    {
      type: "p",
      text:
        "Once your catalog is ready, the next step is deciding how customers will start a purchase. Our guide to [automating sales on Instagram](/blog/how-to-automate-sales-on-instagram) walks through that setup in order.",
    },
  ],
  related: [
    { to: "/catalog-guide", title: "Catalog Guide", description: "Product cards, plan limits, stock, sizes, low-stock alerts and in-DM search." },
    { to: "/public-catalog-guide", title: "Public Catalog Page Guide", description: "What the public catalog page shows and when it refreshes." },
    { to: "/bulk-image-upload-guide", title: "Bulk Photo Upload Guide", description: "Upload up to 50 product photos at once on the Pro plan." },
  ],
  faqs: [
    {
      q: "How many products can I list?",
      a: "The Free plan includes up to 5 products. Creator includes 150, Starter 500, Growth 1,000 and Pro 10,000.",
    },
    {
      q: "Can customers browse products by category inside DMs?",
      a: "Yes. Customers can browse your catalog by category directly in DM, or search by product name, whichever is faster for them.",
    },
    {
      q: "Can I upload many product photos at once?",
      a: "Yes, on the Pro plan. You can upload up to 50 product photos at once and turn them into draft products ready to finish.",
    },
  ],
};

export default post;
